<card
    [changeModeFn]="changeModeFn"
    [showEditSaveButton]="showEditSaveButton()"
    [(mode)]="mode"
    [title]="'Approval Thresholds'"
    [isLoading]="isLoading()"
    [showCancelButton]="true"
>
    <!-- ── VIEW MODE ──────────────────────────────────────────────────── -->
    <card-view-mode-content>
        @if (model(); as model) {
            <div class="flex flex-col gap-medpace">
                <p class="text-mds-grey-600">
                    Thresholds set how many approvals a payment request needs, based on its total amount in USD. They
                    apply to requests with a visit date on or after the effective date.
                </p>

                <ng-container *ngTemplateOutlet="rangeTable; context: { $implicit: rangeRows() ?? [] }" />

                <p class="text-mds-grey-600">
                    Leave a threshold empty to skip its range. With no thresholds set, every request needs 1 coordinator
                    approval.
                </p>

                <!-- Version history -->
                <div class="flex flex-col gap-3">
                    <span class="font-semibold">Timeline</span>

                    @if (model.history.length === 0) {
                        <span data-testid="threshold-history-empty" class="italic text-mds-grey-600"
                            >No threshold history available.</span
                        >
                    }

                    <div class="relative flex flex-col">
                        @if (model.history.length > 1) {
                            <div class="absolute left-[5px] top-3 bottom-3 w-px bg-mds-grey-600"></div>
                        }
                        @for (entry of model.history; track entry.effectiveDate; let first = $first) {
                            <div data-testid="threshold-history-entry" class="relative flex flex-col gap-1 pb-5">
                                <!-- First line: effective date - dot is absolutely positioned to stay centered on this line regardless of its content -->
                                <div class="relative flex flex-row items-center gap-2 pl-6">
                                    <!-- Dot -->
                                    <div
                                        class="absolute left-0 top-1/2 z-10 size-4 -translate-y-1/2 rounded-full flex-shrink-0"
                                        [style.background-color]="
                                            isCurrentEntry(entry, model.current)
                                                ? 'var(--mds-green)'
                                                : 'var(--mds-grey-600)'
                                        "
                                    ></div>
                                    <span class="font-semibold">{{
                                        entry.effectiveDate | dateFormat: undefined : 'local' | uppercase
                                    }}</span>
                                    @if (isCurrentEntry(entry, model.current)) {
                                        <mds-tag
                                            data-testid="threshold-history-active-tag"
                                            class="secondary-tag"
                                            [variant]="'status'"
                                            [statusType]="'success'"
                                            [label]="'Active'"
                                            [categoryIcon]="'check_circle'"
                                        />
                                    }
                                </div>

                                <!-- Content -->
                                <span class="text-mds-grey-600 pl-6">{{ thresholdSummary(entry) }}</span>
                                <div class="flex flex-row items-center gap-2 pl-6">
                                    <span class="text-mds-grey-600"
                                        >{{ entry.modifiedByFullName ?? 'Unknown user' }} · changed
                                        {{
                                            entry.modifiedTimestamp | dateFormat: undefined : 'local' | uppercase
                                        }}</span
                                    >
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        }
    </card-view-mode-content>

    <!-- ── EDIT MODE ──────────────────────────────────────────────────── -->
    <card-edit-mode-content>
        @if (formGroup(); as formGroup) {
            <div class="flex flex-col gap-medpace">
                <p class="text-mds-grey-600">
                    Thresholds set how many approvals a payment request needs, based on its total amount in USD. They
                    apply to requests with a visit date on or after the effective date.
                </p>

                <!-- Input row -->
                <div class="flex flex-row gap-medpace items-start flex-wrap">
                    <div class="flex flex-col gap-1 flex-1">
                        <mds-text-field
                            [formCtrl]="formGroup.controls.threshold1"
                            [appearance]="'outline'"
                            [label]="'Threshold 1 (USD)'"
                            [placeholder]="'Optional'"
                            [inputType]="'decimal'"
                            [decimalPlaces]="2"
                            id="threshold1Input"
                        />
                        <!-- hide small text when errors present to maintain consistent layout - error text will push it down -->
                        <small [class.hidden]="!!formGroup.controls.threshold1.errors" class="text-mds-grey-600 pl-1"
                            >Level 1 ends here</small
                        >
                    </div>
                    <div class="flex flex-col gap-1 flex-1">
                        <mds-text-field
                            [formCtrl]="formGroup.controls.threshold2"
                            [appearance]="'outline'"
                            [label]="'Threshold 2 (USD)'"
                            [placeholder]="'Optional'"
                            [inputType]="'decimal'"
                            [decimalPlaces]="2"
                            id="threshold2Input"
                        />
                        <!-- hide small text when errors present to maintain consistent layout - error text will push it down -->
                        <small [class.hidden]="!!formGroup.controls.threshold2.errors" class="text-mds-grey-600 pl-1"
                            >Level 2 ends here</small
                        >
                    </div>
                    <div class="flex flex-col gap-1 flex-1">
                        <mds-datepicker
                            [formCtrl]="formGroup.controls.effectiveDate"
                            [appearance]="'outline'"
                            [label]="'Effective Date'"
                            [placeholder]="'Select date'"
                            [min]="minEffectiveDate"
                            id="effectiveDateInput"
                        />
                        <!-- hide small text when errors present to maintain consistent layout - error text will push it down -->
                        <small [class.hidden]="!!formGroup.controls.effectiveDate.errors" class="text-mds-grey-600 pl-1"
                            >Applies to visits on or after this date</small
                        >
                    </div>
                    <!-- mt-8 is needed to align horizontally with the mds-text-fields above -->
                    <mds-button
                        class="mt-8"
                        [label]="'Insert'"
                        [variant]="'tonal'"
                        [color]="'primary'"
                        [id]="'insertBtn'"
                        (clickEvent)="insert()"
                    />
                </div>

                <!-- Range table (reflects live threshold, not staged edits) -->
                <ng-container *ngTemplateOutlet="rangeTable; context: { $implicit: liveRangeRows() }" />

                <p class="text-mds-grey-600">
                    Leave a threshold empty to skip its range. With no thresholds set, every request needs 1 coordinator
                    approval.
                </p>

                <!-- Version history with staged rows -->
                <div class="flex flex-col gap-3">
                    <span class="font-semibold">Timeline</span>

                    @if (formGroup.timelineFormGroups().length === 0) {
                        <span data-testid="threshold-history-empty" class="italic text-mds-grey-600"
                            >No threshold history available.</span
                        >
                    }

                    <div class="relative flex flex-col">
                        @if (formGroup.timelineFormGroups().length > 1) {
                            <div class="absolute left-[5px] top-3 bottom-3 w-px bg-mds-grey-600"></div>
                        }
                        @for (row of formGroup.timelineFormGroups(); track row.controls.effectiveDate.value) {
                            <div data-testid="threshold-history-entry" class="relative flex flex-col gap-1 pb-5">
                                <!-- First line: effective date - dot is absolutely positioned to stay centered on this line regardless of its content -->
                                <div class="relative flex flex-row items-center gap-2 pl-6">
                                    <!-- Dot -->
                                    <div
                                        class="absolute left-0 top-1/2 z-10 size-4 -translate-y-1/2 rounded-full flex-shrink-0"
                                        [style.background-color]="
                                            row.isDirty()
                                                ? 'var(--primary)'
                                                : isCurrentRow(row)
                                                  ? 'var(--mds-green)'
                                                  : 'var(--mds-grey-600)'
                                        "
                                    ></div>
                                    <span class="font-semibold">{{
                                        row.controls.effectiveDate.value | dateFormat: undefined : 'local' | uppercase
                                    }}</span>
                                    @if (row.isDirty()) {
                                        <mds-tag
                                            data-testid="threshold-history-pending-tag"
                                            class="secondary-tag"
                                            [variant]="'status'"
                                            [statusType]="'informative'"
                                            [label]="'Pending save'"
                                            [categoryIcon]="'escalator_warning'"
                                        />
                                        <mds-icon-button
                                            data-testid="threshold-history-remove-btn"
                                            [icon]="'delete'"
                                            [color]="'warn'"
                                            [iconAppearance]="'outline'"
                                            (clickEvent)="removeRow(row)"
                                            [tooltip]="'Delete'"
                                        ></mds-icon-button>
                                    }
                                </div>

                                <!-- Content -->
                                <span class="text-mds-grey-600 pl-6">{{ thresholdSummaryFromRow(row) }}</span>
                                @if (!row.isDirty()) {
                                    <div class="flex flex-row items-center gap-2 pl-6">
                                        <span class="text-mds-grey-600"
                                            >{{ row.controls.modifiedByFullName.value }} · changed
                                            {{
                                                row.controls.modifiedTimestamp.value
                                                    | dateFormat: undefined : 'local'
                                                    | uppercase
                                            }}</span
                                        >
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
        }
    </card-edit-mode-content>
</card>

<!-- Shared: Range table (used in both view and edit mode) -->
<ng-template #rangeTable let-rows>
    <div data-testid="threshold-range-table" class="flex flex-col border border-mds-grey-600 rounded">
        <!-- Header -->
        <div
            class="grid grid-cols-3 px-3 py-2 font-semibold uppercase tracking-wide text-mds-grey-600 border-b border-mds-grey-600"
        >
            <span>Amount Range</span>
            <span>Level</span>
            <span class="text-right">Approvals</span>
        </div>
        <mds-divider />
        <!-- Rows -->
        @for (row of rows; track row.levelLabel; let last = $last) {
            <div
                data-testid="threshold-range-row"
                class="grid grid-cols-3 px-3 py-3"
                [class.border-b]="!last"
                [class.border-mds-grey-600]="!last"
            >
                <span>{{ row.rangeLabel }}</span>
                <span class="text-mds-grey-600">{{ row.levelLabel }}</span>
                <span class="text-right font-medium">{{ row.approversLabel }}</span>
            </div>
        }
    </div>
</ng-template>
